import React, { useState, useEffect } from "react";
import { Input, Checkbox, Button, Radio, message } from "antd";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useSearchParams } from "react-router-dom";
import "./panelSurvey.css";
import { FaCheck, FaClock } from "react-icons/fa";

const { TextArea } = Input;

/** ========= PANEL CONFIGURATION ========= **/
const panels: Record<
  string,
  {
    date: string;
    time: string;
    topic: string;
    moderator: string[];
    speaker: string[];
  }
> = {
  "panel-1": {
    date: "2025-11-14",
    time: "10:50 - 11:40",
    topic: "Panel 1: Repositioning the Middle East in a world of uncertainty",
    moderator: [
      "Rania Younes - Sr. Radio Presenter, Dynamic Emcee, Inspiring Motivator & Art of Public Address Expert",
    ],
    speaker: [
      "H.E Raja Al Mazrouei - Chief Executive Officer, Etihad Credit Insurance",
      "Toby Iles - Jadwa’s Chief Economist and a member of the Executive Management Committee",
      "Dr. Jarmo Kotilaine - Strategic Advisor & Author",
      "Ahmed Nassef - Group COO, Dopay",
    ],
  },
  "panel-2": {
    date: "2025-11-14",
    time: "13:00 - 13:50",
    topic:
      "Panel 2: How to make the most of the exceptional location of the Middle East?",
    moderator: ["Mina Nasif - CTP Corporate Treasury & Investment Consultant"],
    speaker: [
      "Simon Williams – Economist for MENA, HSBC",
      "Farouk Soussa - Middle East and North Africa economist, Goldman Sachs",
      "Karim Adouane - Head of Global Markets Trading, Ajman Bank",
    ],
  },
  "panel-3": {
    date: "2025-11-14",
    time: "15:00 - 15:50",
    topic: "Panel 3: Bond trading and market structure – the quiet revolution",
    moderator: [
      "Andy Hill - Managing Director, Co-Head of Market Practice & Regulatory Policy, ICMA",
    ],
    speaker: [
      "Dr. Anita Yadav - CEO, Global Credit Advisory",
      "Sylvain Bigaud - Senior Executive Officer & Regional Head – MEAT, Euroclear Bank",
      "Tahir Aslam - Head of Fixed Income Trading, Global Markets, FAB",
      "Zeina Rizk - Co-Head of Fixed Income and Partner, Amwal Capital Partners",
    ],
  },
  "panel-4": {
    date: "2025-11-14",
    time: "15:50 - 16:45",
    topic: "Panel 4: The role of repo in bond market development",
    moderator: [
      "Sanaa Clausse BenAbdelhadi - Sr. Director & Head of Business Development, ICMA",
    ],
    speaker: [
      "Andrew Stephen - Exec. Director, Buyside Trading Services Sales, JPMorgan Chase",
      "Muhammed Yasir Gorpe - Sr. Investment Specialist, Islamic Development Bank",
      "Omar El Adel - Head of Treasury and Financial Institutions, Arab African International Bank",
      "Muneeb Azhar - Director, Funding Centre, Treasury and Global Markets, Mashreq",
    ],
  },
  "panel-5": {
    date: "2025-11-15",
    time: "10:50 - 11:40",
    topic:
      "Panel 5: Future of technology; How is technology reshaping financial services?",
    moderator: ["Paul Chalmers - CEO of UK Trading Academy"],
    speaker: [
      "Fawaz Ghazal - CEO, Flooss Bahrain BSC",
      "Avramis Despotis - Founder and CEO, Tradepedia",
      "Prof. Uwe Wystup - Professor, FX options pricing and risk management",
      "Ben Humphreys - Head of Data Engineering, Parameta Solutions (TPICAP Group)",
    ],
  },
  "panel-6": {
    date: "2025-11-15",
    time: "13:00 - 13:50",
    topic: "Panel 6: Future Developments of the Global Currency System",
    moderator: ["Rui Correia - CEO & Chair of Board of Education, ACI FMA"],
    speaker: [
      "Paul Blustein - Award-winning Economic Journalist",
      "Nameer Khan - Chairman, MENA Fintech Association",
      "Dr. Tom James - CEO & CIO, Trade Flow Funds",
      "James Kemp - Managing Director, GFMA’s Global FX Division",
    ],
  },
};

const PanelSurvey: React.FC = () => {
  const [searchParams] = useSearchParams();
  const panelId = searchParams.get("id") || "panel-1";
  const panel = panels[panelId];
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tempOpenSurvey, setTempOpenSurvey] = useState<boolean | null>(null); // 🟢 new state

  /** ========= FETCH TEMP FLAG FROM FIRESTORE ========= **/
  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const settingsRef = doc(db, "settings", "settings");
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          setTempOpenSurvey(settingsSnap.data().tempOpenSurvey || false);
        } else {
          setTempOpenSurvey(false);
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
        setTempOpenSurvey(false);
      }
    };
    fetchSetting();
  }, []);

  /** ========= FORM HANDLERS ========= **/
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    rating: "",
    takeaways: "",
    improvements: "",
    comment: "",
  });
  const [features, setFeatures] = useState<string[]>([]);

  const handleChange = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleNext = () => {
    if (step === 1 && (!form.name || !form.email || !form.organization)) {
      message.error("Please fill out all required fields.");
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    if (!form.rating) {
      message.error("Please rate the panel.");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "panel_surveys"), {
        ...form,
        features,
        panelId,
        panelTopic: panel.topic,
        timestamp: new Date().toISOString(),
      });
      setStep(3);
    } catch (err) {
      console.error(err);
      message.error("Failed to submit feedback.");
    } finally {
      setLoading(false);
    }
  };

  /** ========= LOADING STATE ========= **/
  if (tempOpenSurvey === null) {
    return (
      <div className="survey-thankyou-screen">
        <p style={{ color: "#666" }}>Please wait...</p>
      </div>
    );
  }

  /** ========= TIME VALIDATION (DATE + TIME) ========= **/
  const now = new Date();
  const [startStr, endStr] = panel.time.split(" - ");
  const start = new Date(`${panel.date}T${startStr}:00`);
  const end = new Date(`${panel.date}T${endStr}:00`);
  const isBefore = now < start;
  const isAfter = now > end;

  const isWithinTime = now >= start && now <= end;

  /** ========= OVERRIDE: TEMP OPEN ENABLED ========= **/
  const isSurveyOpen = tempOpenSurvey || isWithinTime;

  /** ========= SHOW MESSAGE IF NOT AVAILABLE ========= **/
  if (!isSurveyOpen) {
    return (
      <div className="survey-thankyou-screen">
        <div
          className="thankyou-card"
          style={{
            background: "#003d29",
            color: "#fff",
            textAlign: "center",
            padding: "3rem 2rem",
            borderRadius: "16px",
          }}
        >
          <div
            className="success-checkmark"
            style={{
              background: "radial-gradient(circle, #ffffff 60%, #e6a73b 100%)",
              boxShadow:
                "0 0 0 5px rgba(230,167,59,0.2), 0 0 0 15px rgba(230,167,59,0.15)",
            }}
          >
            <div className="check-icon">
              <FaClock size={45} color="#003d29" />
            </div>
          </div>

          <h2 style={{ color: "#e6a73b", fontSize: 24, marginTop: 12 }}>
            {isBefore ? "Survey not yet available" : "Survey closed"}
          </h2>
          <p style={{ fontSize: 16, color: "#fff" }}>
            {isBefore
              ? "Please check back closer to the session time."
              : "This survey is now closed. Thank you for your interest!"}
          </p>
          <p style={{ marginTop: 12, fontSize: 14, opacity: 0.9 }}>
            Session: {panel.topic}
            <br />
            Schedule: {panel.date} | {panel.time}
          </p>
        </div>
      </div>
    );
  }

  /** ========= STEP 3: THANK YOU ========= **/
  if (step === 3)
    return (
      <div className="survey-thankyou-screen">
        <div className="thankyou-card">
          <div className="success-checkmark">
            <div className="check-icon">
              <FaCheck size={45} className="checkmark-icon" />
            </div>
          </div>

          <h2 style={{ paddingTop: 24 }}>THANK YOU FOR YOUR FEEDBACK!</h2>
          <p>See you at the next panel session!</p>
        </div>
      </div>
    );

  /** ========= STEP 1 & 2 ========= **/
  return (
    <div className="survey-container">
      <div className="survey-content">
        {/* LEFT SIDE – Panel Info */}
        {step === 1 && (
          <div className="survey-info survey-green-panel">
            <div className="survey-logo-wrapper">
              <img
                src="https://res.cloudinary.com/astcdubai/image/upload/v1746430587/45th_Ica_white_logo_xoggsq.png"
                alt="ICA Dubai 2025 Logo"
                className="survey-logo"
              />
            </div>

            <div className="survey-panel-details">
              <div className="survey-time-pill">
                {new Date(panel.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}{" "}
                | {panel.time}
              </div>

              <h3 className="survey-panel-title">{panel.topic}</h3>

              <p className="survey-description">
                What role is the financial sector playing in enabling and
                accelerating these changes? How can we enhance this role and
                make finance a key enabler of change?
              </p>

              <div className="survey-speakers">
                <h4>Speakers:</h4>
                {panel.speaker.map((s, i) => (
                  <p key={i}>{s}</p>
                ))}

                <h4>Moderator:</h4>
                {panel.moderator.map((m, i) => (
                  <p key={i}>{m}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RIGHT SIDE – Survey Form */}
        <div className="survey-form">
          {step === 1 && (
            <>
              <div className="survey-header">
                <h2 style={{ fontSize: 24 }}>Participant Information</h2>
                <p className="subtitle">
                  Please fill in your details to help us record your feedback.
                </p>
              </div>

              <div className="survey-field">
                <label>Full Name</label>
                <Input
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </div>

              <div className="survey-field">
                <label>Email Address</label>
                <Input
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </div>

              <div className="survey-field">
                <label>Organization</label>
                <Input
                  placeholder="Enter your organization"
                  value={form.organization}
                  onChange={(e) => handleChange("organization", e.target.value)}
                />
              </div>

              <div className="survey-actions">
                <Button
                  type="primary"
                  className="survey-btn primary"
                  block
                  onClick={handleNext}
                >
                  Next
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="survey-header">
                <h2 style={{ fontSize: 24 }}>Feedback</h2>
                <p className="subtitle">
                  Your insights help us improve future discussions.
                </p>
              </div>

              <div className="survey-field">
                <label className="survey-field-question">
                  1. How would you rate this panel overall? (1 - lowest, 5 -
                  highest)
                </label>
                <Radio.Group
                  onChange={(e) => handleChange("rating", e.target.value)}
                  value={form.rating}
                  className="numeric-rating"
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <Radio
                      key={num}
                      value={String(num)}
                      className="rating-circle"
                    >
                      {num}
                    </Radio>
                  ))}
                </Radio.Group>
              </div>

              {/* <div className="survey-field">
                <label className="survey-field-question">
                  2. What would you like to see improved in future panels?
                </label>
                <Checkbox.Group
                  value={features}
                  onChange={(checked) => setFeatures(checked as string[])}
                  className="survey-checkbox-group"
                >
                  <Checkbox value="More diverse topics">
                    More diverse topics
                  </Checkbox>
                  <Checkbox value="More Q&A time">More Q&A time</Checkbox>
                  <Checkbox value="Industry insights">
                    More industry insights
                  </Checkbox>
                  <Checkbox value="Interactive sessions">
                    More interactive sessions
                  </Checkbox>
                </Checkbox.Group>
              </div>

              <div className="survey-field">
                <label className="survey-field-question">
                  3. What were your key takeaways?
                </label>
                <TextArea
                  rows={3}
                  placeholder="Share your thoughts..."
                  value={form.takeaways}
                  onChange={(e) => handleChange("takeaways", e.target.value)}
                />
              </div>

              <div className="survey-field">
                <label className="survey-field-question">
                  4. What can we improve in future panels?
                </label>
                <TextArea
                  rows={3}
                  placeholder="Your suggestions..."
                  value={form.improvements}
                  onChange={(e) => handleChange("improvements", e.target.value)}
                />
              </div> */}

              <div className="survey-field">
                <label className="survey-field-question">
                  Additional Comments (Optional)
                </label>
                <TextArea
                  rows={2}
                  placeholder="Anything else you’d like to share?"
                  value={form.comment}
                  onChange={(e) => handleChange("comment", e.target.value)}
                />
              </div>

              <div className="survey-actions">
                <Button
                  className="survey-btn secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button
                  className="survey-btn primary"
                  type="primary"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  Submit Feedback
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PanelSurvey;
